div
<template>
  <ModuleHeader
    :title="$t('courses-management')"
    :sub_title="$t('lesson-details')"
  />

  <Container :is-loading="store.loading || fileUploadLoading">
    <template #content>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <BackButton />
          <EditButton
            @click="
              $router.push({
                name: 'updateLesson',
                params: {
                  course_id: course_id,
                  chapter_id: chapter_id,
                  lesson_id: lesson_id,
                },
              })
            "
          />
        </div>
        <div class="flex items-center gap-4">
          <DeleteButton
            @click="
              () => {
                store
                  .deleteLesson(course_id, chapter_id, lesson_id)
                  .then(() => $router.back());
              }
            "
          />
        </div>
      </div>

      <div
        class="flex items-center justify-start gap-4 my-5"
        v-if="store.uploadAtachmentProgress"
      >
        <h4 class="font-medium text-lg whitespace-nowrap">Upload Progress:</h4>
        <div
          class="block rounded-lg w-full h-5 border shadow-sm "
        >
          <div
            class="bg-success rounded-lg h-full shadow-success shadow-sm  text-white flex items-center justify-end p-2 text-sm"
            :style="`width: ${store.uploadAtachmentProgress || 0}%;`"
          >
            {{ (store.uploadAtachmentProgress || 0) + "%" }}
          </div>
        </div>
      </div>

      <main class="grid grid-cols-12 gap-5 my-5 items-start justify-center">
        <section class="col-span-12 lg:col-span-5 h-full flex flex-col gap-5">
          <div
            class="flex items-center justify-center bg-secondary p-5 rounded-2xl flex-col gap-2 text-primaryDark"
          >
            <div class="bg-white shadow-sm rounded-full p-1">
              <Image
                :src="lesson.photo"
                alt=""
                class="object-cover bg-center rounded-full h-[95px] w-[95px] mx-auto"
              />
            </div>
            <p class="text-lg">{{ lesson.title }}</p>
            <p>{{ lesson.description }}</p>
          </div>
          <div
            class="flex justify-center bg-secondary p-5 rounded-2xl flex-col gap-2 text-primaryDark"
          >
            <p class="text-xs">{{ $t("lesson-details") }}</p>

            <div>
              <label class="text-xs font-light my-1 text-primaryDark">{{
                $t("order")
              }}</label>
              <input
                type="text"
                disabled
                :value="lesson.order"
                class="w-full border border-gray-300 rounded-md p-2 disabled:bg-dark200"
                readonly
              />
            </div>
            <div>
              <label class="text-xs font-light my-1 text-primaryDark">{{
                $t("duration")
              }}</label>
              <input
                type="text"
                disabled
                :value="lesson.duration"
                class="w-full border border-gray-300 rounded-md p-2 disabled:bg-dark200"
                readonly
              />
            </div>
            <div>
              <label class="text-xs font-light my-1 text-primaryDark">{{
                $t("resolution")
              }}</label>
              <input
                type="text"
                disabled
                :value="getVideoResolutionName(lesson.resolution)"
                class="w-full border border-gray-300 rounded-md p-2 disabled:bg-dark200"
                readonly
              />
            </div>
            <div>
              <label class="text-xs font-light my-1 text-primaryDark">{{
                $t("is-preview")
              }}</label>
              <select
                disabled
                :value="lesson.isPreview"
                class="w-full border border-gray-300 rounded-md p-2 disabled:bg-dark200"
                readonly
              >
                <option :value="true">
                  {{ $t("yes") }}
                </option>
                <option :value="false">
                  {{ $t("no") }}
                </option>
              </select>
            </div>
            <div>
              <label class="text-xs font-light my-1 text-primaryDark">{{
                $t("attachment")
              }}</label>
              <div class="flex items-center gap-4">
                <button
                  :disabled="!lesson.attachmentPath"
                  @click="downloadFile(lesson.attachmentPath)"
                  class="button-primary"
                >
                  Download
                </button>
                <DeleteButton
                  v-if="lesson.attachmentPath"
                  @click="
                    () => {
                      store.deleteContent(course_id, lesson_id).then(() => {
                        lesson.attachmentPath = '';
                      });
                    }
                  "
                />
              </div>
            </div>
          </div>
        </section>
        <section class="col-span-12 lg:col-span-7 overflow-hidden h-full">
          <div class="my-5">
            <label for="uploadFile">
              {{ $t("upload-attachment") }}
            </label>
            <label
              for="uploadFile"
              class="border-4 col-span-1 border-primary relative w-full mx-auto text-primary h-64 bg-secondary flex items-center justify-center rounded-lg border-dashed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                style=" fill: currentColor"
              >
                <path d="M11 15h2V9h3l-4-5-4 5h3z"></path>
                <path
                  d="M20 18H4v-7H2v7c0 1.103.897 2 2 2h16c1.103 0 2-.897 2-2v-7h-2v7z"
                ></path>
              </svg>
            </label>
            <input
              @change="
                (event) => {
                  handleAttachmentChange(event);
                }
              "
              class="hidden"
              type="file"
              multiple
              accept="video/mp4,video/x-m4v,video/*"
              name="uploadFile"
              id="uploadFile"
            />
          </div>
          <div>
            <label for="contentUrl"> {{ $t("contentUrl") }} </label>
            <input
                type="text"
                v-model="state.contentUrl"
                class="w-full border-primaryDark border-2 rounded-lg p-2"
            />
          </div>
          <hr>
          <div class="my-5">
            <label for="resolution"> {{ $t("resolution") }} </label>
            <Field
              as="select"
              name="resolution"
              id="resolution"
              v-model="state.resolution"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            >
              <option
                v-for="(item, index) of getVideoResolutionOption()"
                :key="index"
                :value="item.value"
              >
                {{ item.label }}
              </option>
            </Field>
            <ErrorMessage class="error-message" name="resolution" />
          </div>
          <div>
            <label for="duration"> {{ $t("duration") }} </label>
            <input
              type="text"
              v-model="state.duration"
              class="w-full border-primaryDark border-2 rounded-lg p-2"
            />
          </div>
          <div>
            <button class="button-primary" @click="submit">Submit</button>
          </div>
        </section>
      </main>
    </template>
  </Container>
</template>

<script setup lang="ts">
import { useCourse } from "../store/courseStore";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { onMounted, reactive, ref, watch } from "vue";
import { Lesson } from "../models/lesson";
import { downloadFile } from "@/core/utils/functions";
import { CreateCourseContentRequestDto } from "../models/content";
import {
  getVideoResolutionOption,
  VideoResolution,
  getVideoResolutionName,
} from "@/core/enums/VideoResolution";
import { Field, Form, ErrorMessage } from "vee-validate";
import { getVideoDuration } from "../../../core/utils/functions";

const store = useCourse();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const fileUploadLoading = ref(false);

const course_id = route.params.course_id as string;
const chapter_id = route.params.chapter_id as string;
const lesson_id = route.params.lesson_id as string;

const state = reactive({
  attachment: null as File | null,
  contentUrl: "",
  resolution: VideoResolution.P1080,
  duration: 0,
});

watch(
  () => store.uploadAtachmentProgress,
  (progress) => {
    if (progress > 0) {
      window.history.pushState(null, "", window.location.href);
      window.onpopstate = function () {
        window.history.pushState(null, "", window.location.href);
      };

      window.addEventListener("beforeunload", function (event) {
        event.preventDefault();
      });

      if (progress === 100) {
        window.removeEventListener("beforeunload", function (event) {
          event.preventDefault();
        });
      }
    }
  }
);

const lesson = reactive<Lesson>({
  id: "",
  title: "",
  order: 0,
  attachmentPath: "",
  isPreview: false,
  chapterId: "",
  description: "",
  courseId: "",
  duration: 0,
  photo: "",
  resolution: VideoResolution.P1080,
});

onMounted(() => {
  store
    .fetchLesson(course_id, chapter_id, lesson_id)
    .then((chapter) => {
      lesson.id = chapter.id;
      lesson.title = chapter.title;
      lesson.order = chapter.order;
      lesson.isPreview = chapter.isPreview;
      lesson.photo = chapter.photo;
      lesson.description = chapter.description;
      lesson.duration = chapter.duration;
      lesson.resolution = chapter.resolution;
      lesson.attachmentPath = chapter.attachmentPath;
      lesson.chapterId = chapter.chapterId;
      lesson.courseId = chapter.courseId;
      state.contentUrl = chapter.attachmentPath;
    })
    .catch((error: any) => {
      toast.error(
        error?.response?.data?.message || "Error fetching lesson data"
      );
      router.back();
    });
});

const handleAttachmentChange = async (event: Event) => {
  fileUploadLoading.value = true;
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;
  state.attachment = file;

  try {
    let duration = await getVideoDuration(file);
    // duration in seconds
    state.duration = duration;
  } catch (error: any) {
    toast.error("Error getting video duration");
  } finally {
    fileUploadLoading.value = false;
  }
  
};

const submit = async () => {
  try {
    const request: CreateCourseContentRequestDto = {
      attachment: state.attachment as File,
      courseId: course_id,
      lessonId: lesson_id,
      resolution: state.resolution,
      // numberOfSeconds: 10,
      contentUrl: state.contentUrl,
      numberOfSeconds: parseInt(state.duration.toFixed()),
    };

    await store.uploadLessonAttachment(course_id, lesson_id, request);
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Error uploading attachment");
  }
};
</script>
